import { Injectable } from "@angular/core";

import {
  DataService as CoreDataService,
  IPersistable,
  ObjectKey,
  PersistenceService,
  PersistMode,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
} from "@barthauer/abc/core/data";

import { GUID } from "@barthauer/abc/core";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { catchError, first, map, switchMap, tap } from "rxjs/operators";
import { FavoriteDataSource } from "./favorite-dataSource";
import { BoxDetailViewModel } from "./Model/box.viewModel";
import { ObjectTypeService } from "../../services/object-type.service";
import { ObjectKeys } from "../../services/data.service";

@Injectable({
  providedIn: "root",
})
export class FavoritesService {
  private favorites: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public favorites$ = this.favorites.asObservable();

  public allFavorites: FavoriteDataSource;
  private boxIdFavoriten: string;
  public appObjectTypesKeys: string[] = [];
  constructor(
    private dataService: CoreDataService,
    private persistenceService: PersistenceService,
    private objectTypeService: ObjectTypeService,
  ) {}
  public initial() {
    this.favorites = new BehaviorSubject<any>(null);
    this.favorites$ = this.favorites.asObservable();
  }

  public resetFavorites(): void {
  this.allFavorites = null;
  this.favorites.next(null);
}
  public getDataSource(): Observable<FavoriteDataSource> {
    if (this.allFavorites) {
      return of(this.allFavorites);
    } else {
      return this.dataService
        .executeReadObjectsQuery(this.prepareQueryFavorites())
        .pipe(
          switchMap((data: any) => {
            this.allFavorites = new FavoriteDataSource(data);

            const displayTextObservables: Observable<any>[] = [];

            for (const key in this.allFavorites.favoritesByObjectType) {
              if (
                Object.prototype.hasOwnProperty.call(
                  this.allFavorites.favoritesByObjectType,
                  key,
                ) &&
                this.appObjectTypesKeys.includes(key)
              ) {
                const boxDetailViewModel: BoxDetailViewModel[] =
                  this.allFavorites.favoritesByObjectType[key];
                let id: string = "( ";
                boxDetailViewModel.forEach((favorite) => {
                  id = id + " '" + favorite.ParentId + "' ,";
                });
                id = id.slice(0, -1) + ")";

                displayTextObservables.push(this.getDisplayText(key, id));
              }
            }

            return forkJoin(displayTextObservables).pipe(
              tap(() => {
                this.favorites.next(null);
                this.favorites.next(this.allFavorites);
              }),
              map(() => null),
            );
          }),
        );
    }
  }

  private prepareQueryFavorites(): QueryObjectsModel {    
    const projectId = sessionStorage.getItem("projectId");
    const queryColumns: Array<QueryColumn> = [
      this.dataService.createQueryColumn(
        "ParentObjectType",
        "ParentObjectType",
      ),
      this.dataService.createQueryColumn("ParentId", "ParentId"),
          
      this.dataService.createQueryColumn("Id", "Id"),
      this.dataService.createQueryColumn("gebId", "gebId"),
    ];
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = "BoxDetail";
    mainQuery.OPath = `
     Box.ParentId = '${localStorage.getItem("profileId")}'
      And Box.Designation = 'Favoriten'
    And CaseWhen(
        ParentObjectType,
        ${this.objectTypeService.getObjectTypesValue(ObjectKeys.SUBPROJECT)}, !.Cast(xmtSubProject).ParentId = '${projectId}',
        ${this.objectTypeService.getObjectTypesValue(ObjectKeys.MEASURE)},  !.Cast(xmtMeasure).SubProject.ParentId = '${projectId}',
       ${this.objectTypeService.getObjectTypesValue(ObjectKeys.MEASURETASK)},  !.Cast(xmtMeasureTask).!.Cast(xmtMeasure).SubProject.ParentId = '${projectId}',false
      )
`;

    mainQuery.Columns = queryColumns;

    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];

    return queryDocument;
  }

  private getDisplayText(objectType: string, ids: string): Observable<any> {
    return this.dataService
      .executeReadObjectsQuery(
        this.prepareQueryToGetDisplayText(objectType, ids),
      )
      .pipe(
        map((data) => {
          this.allFavorites?.setDisplayText(objectType, data);
          return data;
        }),
      );
  }

  private prepareQueryToGetDisplayText(
    ObjectType: string,
    ids: string,
  ): QueryObjectsModel {
    let queryColumns: Array<QueryColumn> = [];

    if (ObjectType === this.objectTypeService.getObjectTypesValue(ObjectKeys.MEASURETASK).toString()) {
      queryColumns = [
        this.dataService.createQueryColumn("Id", "Id"),
        this.dataService.createQueryColumn(
          "Type(xmtMeasure)[Id= ^.ParentId].Id",
          "MeasureTaskParentId",
        ),
        this.dataService.createQueryColumn(
          `Concat(IIF(ParentObjectType = '${this.objectTypeService.getObjectTypesValue(ObjectKeys.MEASURE)}', Type(xmtMeasure)[Id= ^.ParentId].Designation,Type(Person)[Id= ^.ParentId].GetValue(ref:Id)), ' / ', Subject, ' / ', CaseNull(ToStr(StartDate), '-'), ' / ', CaseNull(ToStr(EndDate), '-'), ' / ', Type)`,
          "DisplayText",
          QueryColumnSortOrder.Ascending,
        ),
      ];
    } else {
      queryColumns = [
        this.dataService.createQueryColumn("Id", "Id"),
        this.dataService.createQueryColumn(
          "ref:Id",
          "DisplayText",
          QueryColumnSortOrder.Ascending,
        ),
      ];
    }

    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectType;

    mainQuery.OPath = `Id IN ${ids}`;
    mainQuery.Columns = queryColumns;

    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];

    return queryDocument;
  }

  private getBoxId(): Observable<any> {
    return this.dataService.executeReadObjectsQuery(this.prepareBoxId()).pipe(
      map((data: any) => {
        if (data && data.length > 0) {
          this.boxIdFavoriten = data[0]?.Id;

          return this.boxIdFavoriten;
        } else {
          const newBoxId = GUID.newGUID();

          return this.persistBoxFavorite(newBoxId);
        }
      }),
      catchError((err) => {
        console.error("Error in getBoxId:", err);
        throw err;
      }),
    );
  }

  private prepareBoxId(): QueryObjectsModel {
    const queryColumns: Array<QueryColumn> = [
      this.dataService.createQueryColumn("Id", "Id"),
    ];
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = "Box";
    mainQuery.OPath =
      "ParentId = '" +
      localStorage.getItem("profileId") +
      "' AND Designation = 'Favoriten'";
    mainQuery.Columns = queryColumns;
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  public deleteFavoriten(id: string) {
    const objectKey: IPersistable = {
      id: id,
      objectType: "BoxDetail",
      persistenceCategory: null,
      changesPersisted: (persistMode: PersistMode) =>
        (persistMode = PersistMode.Delete),
      revertChanges: () => {},
    };

    this.persistenceService.clearPendingObjects();
    this.persistenceService.addObjectForDelete(objectKey);
    this.allFavorites?.deleteFavoriteFromList(id);

    this.favorites.next(this.allFavorites);
    this.persistenceService.persistPendingChanges();
  }

  private persistBoxFavorite(boxIdFavoriten: string): string {
    const objectKey: IPersistable = {
      id: boxIdFavoriten,
      objectType: "Box",
      changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
      revertChanges: () => {},
    };
    try {
      this.persistenceService.clearPendingObjects();
      this.persistenceService.addObjectForInsert(objectKey);
      this.persistenceService.addObjectValue(
        objectKey,
        "Designation",
        "Favoriten",
      );
      this.persistenceService.addObjectValue(
        objectKey,
        "ParentId",
        localStorage.getItem("profileId"),
      );
      this.persistenceService.addObjectValue(objectKey, "Number", 1);
      this.persistenceService.addObjectValue(objectKey, "Type", "0");
      this.persistenceService.persistPendingChanges();

      return boxIdFavoriten;
    } catch (err) {
      console.error("Error in persistBoxFavorite:", err);
      throw err;
    }
  }

  public persistFavoriten(
    id: string,
    parentObjectType: string | number,
    parentId: string,
    designation: string,
  ): Observable<BoxDetailViewModel> {
    const objectKey: IPersistable = {
      id: id,
      objectType: "BoxDetail",
      changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
      revertChanges: () => {},
    };

    return (
      this.boxIdFavoriten ? of(this.boxIdFavoriten) : this.getBoxId()
    ).pipe(
      switchMap((data: string) => {
        if (data) {
          this.boxIdFavoriten = data;
          this.persistenceService.addObjectForInsert(objectKey);
          this.persistenceService.addObjectValue(
            objectKey,
            "ParentObjectType",
            parentObjectType,
          );
          this.persistenceService.addObjectValue(
            objectKey,
            "ParentId",
            parentId,
          );

          this.persistenceService.addObjectValue(
            objectKey,
            "gebId",
            this.boxIdFavoriten,
          );
        }
        this.persistenceService.persistPendingChanges();

        return this.persistenceService.afterPersistPendingChanges$.pipe(
          first(),
          map(() => {
            const BoxDetail = {
              Id: id,
              ParentId: parentId,
              ParentObjectType: parentObjectType,
              gebId: this.boxIdFavoriten,
              DisplayText: designation,
            };

            this.allFavorites?.favorites?.set(
              id,
              new BoxDetailViewModel(BoxDetail),
            );
            this.favorites.next(this.allFavorites);

            return new BoxDetailViewModel(BoxDetail);
          }),
        );
      }),
    );
  }
}

export class TargetObjectData {
  ObjectKey: ObjectKey;
  Mode: PersistMode;
  TargetColumns: TargetColumnValue[];
}

export class TargetColumnValue {
  Name: string;
  Value: any;
}
